import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL } from "./apiConfig";
import { useDispatch } from "react-redux";

// Define the initial state for the authentication part of the Redux store
const initialState = {
  isLoggedIn: false, // Tracks whether the user is logged in
  isLoading: false, // Indicates if an async operation is in progress
  error: null, // Holds error messages, if any
  adminKey: null, // Holds the authentication token or key
};

// Async thunk for validating login credentials
export const validateLogin = createAsyncThunk("auth/validateLogin", async ({ username, password }, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append("username", username);
  formData.append("password", password);

  // Attempt to POST login credentials to the server
  return fetch(BASE_URL + "/admin/validate", {
    method: "POST",
    body: formData,
  })
    .then((res) => {
      if (!res.ok) {
        // Manually throw an error if the HTTP response is not ok
        return rejectWithValue(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then((data) => {
      if (!data.success) {
        return rejectWithValue(data.message === "Invalid credentials provided. Access denied." ? "The username or password is incorrect." : data.message);
      }
      return data.admin_id;
    })
    .catch((error) => {
      return rejectWithValue(error.message);
    });
});

// Slice definition for authentication
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Initialize auth state from a cookie or persisted state
    initializeAuthFromCookie: (state, action) => {
      state.adminKey = action.payload;
      state.isLoggedIn = !!action.payload;
    },
    // Handle user logout
    logout: (state) => {
      state.isLoggedIn = false;
      state.adminKey = null;
      state.error = null;
    },
    // Clear any authentication errors
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateLogin.pending, (state) => {
        state.isLoading = true; // Mark as loading on request start
      })
      .addCase(validateLogin.fulfilled, (state, action) => {
        state.isLoading = false; // Remove loading indicator on success
        state.isLoggedIn = true; // Set login status to true
        state.adminKey = action.payload; // Store the admin key
        state.error = null; // Clear any errors
      })
      .addCase(validateLogin.rejected, (state, action) => {
        console.log("Rejected action:", action.payload);
        state.isLoading = false; // Remove loading indicator on failure
        state.error = action.payload; // Store the error message from the rejected action
        console.log(action.payload);
      });
  },
});

// Export the action creators
export const { initializeAuthFromCookie, logout, clearError } = authSlice.actions;

// Export the reducer as the default export
export default authSlice.reducer;
